/**
 * Integrytas Group - Base App (MOBILE)
 *
 * Related-children detail block for mobile list screens.
 *
 * The mobile counterpart of the desktop side-by-side RelatedChildrenPanel. On a
 * phone, side-by-side cards do not fit (375px), and the mobile idiom is
 * tap-row -> bottom-sheet detail - so a parent's children live INSIDE its detail
 * sheet, rendered as STACKED sections (uppercase header + count over a bordered
 * list). Not tabbed: the child lists are short, complementary facets of one
 * entity, so stacking keeps them all scannable with no extra taps.
 *
 * A page lazy-loads the selected entity's children when the sheet opens and
 * hands them here as MobileChildGroup[]; this component only presents them.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthSession } from '../../types/auth';
import { MobileChildDetailSheet, type MobileChildTarget } from './MobileChildDetailSheet';

export interface MobileChildItem {
  id: string | number;
  label: string;          // resolved display name - never a raw id/code
  sub?: string;           // secondary line (e.g. code, email)
  badges?: string[];      // compact mono badges (perms V/A/C/D/S, a sequence #)
  /**
   * Optional leading glyph - a locale flag, a module icon. Decoration only: the
   * resolved `label` still carries the meaning, so the row stays readable when a
   * glyph cannot render (§17.B4). Mirrors the desktop panel's `icon`.
   */
  icon?: string;
}

export interface MobileChildGroup {
  key: string;
  title: string;          // section header, localised by the caller
  items: MobileChildItem[];
  /**
   * Drill-down identity — the same pair the desktop panel takes. With both
   * present the rows become tappable and open MobileChildDetailSheet; without
   * them the group renders exactly as before and is NOT tappable, because a
   * tap that does nothing is worse than no affordance.
   */
  table?: string;
  pkColumn?: string;
}

export function MobileRelatedChildren({
  groups, busy, emptyLabel, session, onSaved,
}: {
  groups: MobileChildGroup[];
  busy?: boolean;
  emptyLabel?: string;
  /** Supply to let a registered mobile editor open instead of the read-only sheet. */
  session?: AuthSession;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState<MobileChildTarget | null>(null);

  if (busy) {
    return <div className="m-rc-loading">{t('mobile.related.loading', 'Loading related…')}</div>;
  }
  const nonEmpty = groups.filter(g => g.items.length > 0);
  if (nonEmpty.length === 0) {
    return <div className="m-rc-empty">{emptyLabel ?? t('mobile.related.none', 'No related records')}</div>;
  }
  return (
    <div className="m-rc">
      {nonEmpty.map(g => {
        // Narrow once rather than asserting non-null at the call site — the
        // repo forbids `!`, and this also keeps the pair inseparable.
        const drill = g.table && g.pkColumn ? { table: g.table, pkColumn: g.pkColumn } : null;
        return (
        <div key={g.key} className="m-rc-sect">
          <div className="m-rc-head">
            <span>{g.title}</span>
            <span className="m-rc-count">{g.items.length}</span>
          </div>
          <div className="m-rc-list">
            {g.items.map(it => (
              <div
                key={it.id}
                className={'m-rc-item' + (drill ? ' m-rc-item-tap' : '')}
                onClick={drill
                  ? () => setTarget({ table: drill.table, pkColumn: drill.pkColumn, pk: it.id, label: it.label })
                  : undefined}
                role={drill ? 'button' : undefined}
                tabIndex={drill ? 0 : undefined}
              >
                <span className="m-rc-name">
                  <span className="m-rc-t" title={it.sub}>
                    {it.icon && <span className="m-rc-icon" aria-hidden>{it.icon}</span>}
                    {it.label}
                  </span>
                  {it.sub && <span className="m-rc-sub">{it.sub}</span>}
                </span>
                {it.badges && it.badges.length > 0 && (
                  <span className="m-rc-badges">
                    {it.badges.map(b => <span key={b} className="m-rc-badge">{b}</span>)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        );
      })}
      <MobileChildDetailSheet target={target} onClose={() => setTarget(null)} session={session} onSaved={onSaved} />
    </div>
  );
}
